import {
  Component,
  Input,
  Output,
  EventEmitter,
  CUSTOM_ELEMENTS_SCHEMA,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import type { LeafDatePicker } from '@cigna/leaf-web/lib/wc';
import '@cigna/leaf-web/lib/wc/components/date-picker/date-picker';

@Component({
  selector: 'cigna-leaf-date-picker',
  template: `
    <leaf-date-picker
      [label]="label"
      [placeholder]="placeholder"
      [value]="value"
      [min]="min"
      [max]="max"
      [disabledDates]="disabledDates"
      [hasShortcuts]="hasShortcuts"
      [ariaDescribedBy]="ariaDescribedBy"
      [ariaLive]="ariaLive"
      [validationAriaDescribedBy]="validationAriaDescribedBy"
      [isError]="isError"
      [isSuccess]="isSuccess"
      [errorNote]="isError ? errorNote : undefined"
      [successNote]="isSuccess ? successNote : undefined"
      [successText]="successText"
      [disabled]="disabled"
      [readonly]="readonly"
      [required]="required"
      [fieldId]="fieldId"
      [fieldNote]="fieldNote"
      [name]="name"
      (leaf-blur)="inputHandler($event?.detail?.value)"
    ></leaf-date-picker>
  `,
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CignaLeafDatePickerComponent),
      multi: true,
    },
  ],
})
export class CignaLeafDatePickerComponent implements ControlValueAccessor {
  @Input() label: LeafDatePicker['label'];
  @Input() placeholder: LeafDatePicker['placeholder'];
  @Input() min: LeafDatePicker['min'];
  @Input() max: LeafDatePicker['max'];
  @Input() disabledDates: LeafDatePicker['disabledDates'];
  @Input() hasShortcuts: LeafDatePicker['hasShortcuts'];

  @Input() ariaDescribedBy: LeafDatePicker['ariaDescribedBy'];
  @Input() ariaLive: LeafDatePicker['ariaLive'];
  @Input()
  validationAriaDescribedBy: LeafDatePicker['validationAriaDescribedBy'];

  @Input() isError: LeafDatePicker['isError'];
  @Input() errorNote: LeafDatePicker['errorNote'];
  @Input() isSuccess: LeafDatePicker['isSuccess'];
  @Input() successText: LeafDatePicker['successText'];
  @Input() successNote: LeafDatePicker['successNote'];

  @Input() disabled: LeafDatePicker['disabled'];
  @Input() readonly: LeafDatePicker['readonly'];
  @Input() required: LeafDatePicker['required'];
  @Input() fieldId: LeafDatePicker['fieldId'];
  @Input() fieldNote: LeafDatePicker['fieldNote'];
  @Input() name: LeafDatePicker['name'];

  /**
   * ********
   * CUSTOM ANGULAR PROPERTIES
   */
  @Output() change = new EventEmitter<string>();

  private _value = '';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: any = () => {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onTouched: any = () => {};

  inputHandler(val: string) {
    this._value = val;
    this.onTouched();
    this.onChange(this._value);
    this.change.emit(val);
  }

  @Input()
  get value() {
    return this._value;
  }

  set value(val: string) {
    if (val !== this._value) {
      this._value = val;
      this.onTouched();
    }
  }

  writeValue(value: string) {
    this.value = value;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
